import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import './styles/index.scss'
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import './i18n'
import store from './store/store'
import { Provider } from 'react-redux'
import App from './App'
import { changeTheme } from './utils/theme'
import * as Sentry from '@sentry/react'
import { shortenEnvironment } from './utils/helpers'
import { environment } from './config'
import { getAppFeatures } from '@utils/getAppFeatures'
import { localStorageService } from '@services'
import { logger } from '@utils/logger'
import { getWebConfigParams } from '@utils/getWebConfigParams'
import { createAppManager } from '@utils/appManager'
import { RouteTracker } from '@shared/components/RouteTracker/RouteTracker'

const config = getWebConfigParams()

/**
 * Get client features.
 */
const features = getAppFeatures()
const sentryDSN = features?.infra?.sentryDSN

if (sentryDSN && environment?.env === 'production') {
  logger(`[Sentry] - Creating Sentry instance`)
  Sentry.init({
    dsn: features?.infra?.sentryDSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        enableHTTPTimings: true,
      }),
    ],
    debug: false,
    tracesSampleRate: 0.5,
    maxBreadcrumbs: 50,
    environment: shortenEnvironment(environment?.env),
  })
}

/**
 * Retrieve storage settings
 */
const theme = config?.theme || localStorageService?.getLocalState()?.theme
if (theme) {
  changeTheme(theme)
} else {
  const preferDark = '(prefers-color-scheme: dark)'
  if (window?.matchMedia) {
    const initialTheme = window?.matchMedia(preferDark).matches
      ? 'dark'
      : 'light'
    if (initialTheme) {
      document
        ?.getElementsByTagName('body')?.[0]
        ?.setAttribute('data-theme', initialTheme)
    }
  }
}

export const manager = createAppManager(config?.mode, config?.debug)

const root = ReactDOM.createRoot(document.getElementById('root'))

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <RouteTracker />
        <App
          manager={manager}
          embedded={manager?.embedded}
          defaultLang={config?.lang}
        />
      </BrowserRouter>
    </Provider>
  </React.StrictMode>,
)
